import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Seo from "@/components/feature/Seo";
import BackToTop from "@/components/feature/BackToTop";

const values = [
  {
    icon: "ri-eye-line",
    title: "客观真实",
    desc: "所有评测都基于实际使用体验，从广告、速度、内容到良心度逐一打分，不偏袒任何站点。",
  },
  {
    icon: "ri-settings-3-line",
    title: "维度清晰",
    desc: "用统一的评测维度横向对比，让不同站点之间的差距一目了然，方便你按需求挑选。",
  },
  {
    icon: "ri-shield-check-line",
    title: "少踩坑",
    desc: "持续更新避雷信息，帮你看清套路与坑点，把精力留给真正值得看的作品。",
  },
  {
    icon: "ri-heart-line",
    title: "尊重版权",
    desc: "倡导在享受内容的同时尊重创作者，优先支持正版渠道，让良心内容可持续。",
  },
];

const praises = [
  { text: "按「广告少」排序选站太方便了，终于不用被弹窗轰炸。", author: "追番爱好者 · 阿凯" },
  { text: "避雷指南很实用，读完学会了识别套路，少踩了很多坑。", author: "条漫党 · 小鹿" },
  { text: "评测维度很清楚，加载速度的实测数据对选择很有帮助。", author: "补番党 · 老周" },
  { text: "界面清爽，评测也够客观，是我选站必看的参考。", author: "国漫迷 · 柠檬" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于漫界指南",
          url: "https://kmniy25.cn/about",
          description: "漫界指南是一个专注漫画网站推荐与避雷的评测平台，从广告控制、加载速度、内容收录与良心程度四个维度提供客观评测。",
        }}
      />
      <Navbar />

      <main className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">关于我们</span>
        </nav>

        {/* intro */}
        <section className="mt-6 overflow-hidden rounded-2xl bg-foreground-950">
          <div className="flex flex-col items-start gap-6 p-6 md:flex-row md:items-center md:justify-between md:p-10">
            <div className="max-w-2xl">
              <h1 className="font-display text-3xl text-background-50 md:text-4xl">关于漫界指南</h1>
              <p className="mt-4 text-sm leading-7 text-background-200">
                漫界指南是一个专注「漫画网站推荐与避雷」的评测平台。我们理解想找好用的漫画网站有多不容易——广告满天飞、加载慢、套路多。
                因此我们实地体验大量站点，从广告控制、加载速度、内容收录与良心程度四个维度给出客观评测，帮你筛选真正好用的良心站，让看漫画更省心、少踩坑。
              </p>
            </div>
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-primary-500 text-background-50">
              <i className="ri-book-open-line text-4xl"></i>
            </div>
          </div>
        </section>

        {/* positioning */}
        <section className="mt-12">
          <h2 className="font-display text-2xl text-foreground-950 md:text-3xl">网站定位与理念</h2>
          <p className="mt-2 max-w-2xl text-sm leading-7 text-foreground-600">
            我们不追流量、不堆噱头，只做一件事：把真实体验整理成清晰、可用的评测，帮助每位漫画爱好者在海量站点中快速找到适合自己的选择。
            每一篇评测、每一条避雷建议，都围绕「广告多不多、加载速度快不快、是否良心」这几个用户最关心的问题展开。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                  <i className={v.icon}></i>
                </span>
                <h3 className="mt-4 font-display text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* praises */}
        <section className="mt-12 border-t border-background-200 pt-12">
          <h2 className="font-display text-2xl text-foreground-950 md:text-3xl">用户口碑</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {praises.map((p) => (
              <figure key={p.author} className="rounded-xl border border-background-200 bg-background-100 p-5">
                <i className="ri-double-quotes-l text-2xl text-secondary-400"></i>
                <blockquote className="mt-2 text-sm leading-7 text-foreground-700">{p.text}</blockquote>
                <figcaption className="mt-3 text-xs text-foreground-500">— {p.author}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* contact */}
        <section className="mt-12 overflow-hidden rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="font-display text-xl text-foreground-950 md:text-2xl">联系我们</h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-foreground-600">
            如果你有想要评测的漫画网站，或对现有评测有不同看法，欢迎联系我们反馈。你的建议是我们做得更好的动力。
          </p>
          <div className="mt-5 flex flex-col gap-3 text-sm text-foreground-700 sm:flex-row sm:items-center">
            <span className="inline-flex items-center gap-2">
              <i className="ri-mail-line text-primary-600"></i>
              hello@manjie-guide.example
            </span>
            <span className="hidden h-4 w-px bg-background-300 sm:block"></span>
            <span className="inline-flex items-center gap-2">
              <i className="ri-chat-3-line text-accent-600"></i>
              评测建议与避雷反馈
            </span>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/rankings"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-trophy-line"></i>
              查看榜单
            </Link>
            <Link
              to="/guide"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-medium text-foreground-900 ring-1 ring-background-300 transition-colors hover:bg-background-50/70"
            >
              <i className="ri-shield-check-line"></i>
              避雷指南
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}